import { t } from '@lingui/macro'
import CloseIcon from '@mui/icons-material/Close'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    LinearProgress,
    TextField,
    Typography
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { AspenPatientsService, CarePartnerResponseDTO, CarePartnersService, PatientDTO } from '../client'
import { useCreateAndAssignPatientCarePartner } from '../hooks/useCreateAndAssignPatientCarePartner'
import { PatientSearchDropdown } from './PatientSearchDropdown'

interface CreatePatientCarePartnerModalProps {
    open: boolean
    onClose: () => void
    onSuccess?: (carePartnerId: string) => void
    patientId?: string
    mode?: 'create' | 'edit'
    carePartner?: CarePartnerResponseDTO | null
    assignedPatientId?: string // ID of the currently assigned patient (if any)
}

interface FormData {
    firstName: string
    lastName: string
    phoneNumber: string
    email: string
    assignedPatient: PatientDTO | null
}

// Email validation using the exact same pattern as central-site (Yup's email validation)
const isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false

    // Use the same simple email validation as central-site
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
}

// Phone validation using the exact same pattern as central-site
const isValidPhone = (phone: string): boolean => {
    if (!phone || typeof phone !== 'string') return false
    // Exact same regex as central-site: /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/
    const phoneRegex = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/
    return phoneRegex.test(phone)
}

const validateForm = (data: FormData, requirePatient: boolean = true): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!data.firstName?.trim()) {
        errors.firstName = t`First name is required`
    } else if (data.firstName.length > 50) {
        errors.firstName = t`First name must be 50 characters or less`
    }
    if (!data.lastName?.trim()) {
        errors.lastName = t`Last name is required`
    } else if (data.lastName.length > 50) {
        errors.lastName = t`Last name must be 50 characters or less`
    }
    // Phone validation - exact same as central-site
    if (!data.phoneNumber?.trim()) {
        errors.phoneNumber = t`Phone number is required`
    } else if (!isValidPhone(data.phoneNumber)) {
        errors.phoneNumber = t`Format 123-456-7890`
    }

    // Email validation - same as central-site (Yup's email validation)
    if (data.email && !isValidEmail(data.email)) {
        errors.email = t`Invalid email address`
    } else if (data.email && data.email.length > 256) {
        errors.email = t`Email must be 256 characters or less`
    }

    if (requirePatient && !data.assignedPatient) {
        errors.assignedPatient = t`Please select a patient`
    }

    return errors
}

export const CreatePatientCarePartnerModal: React.FC<CreatePatientCarePartnerModalProps> = ({
    open,
    onClose,
    onSuccess,
    patientId,
    mode = 'create',
    carePartner,
    assignedPatientId
}) => {
    const { createAndAssign, updateCarePartnerData, loading, error } = useCreateAndAssignPatientCarePartner()
    const [submitError, setSubmitError] = useState<string | null>(null)

    // Function to fetch patient data for edit mode
    const fetchPatientData = useCallback(async (patientId: string) => {
        try {
            const response = await AspenPatientsService.getPatientsPatientIdGet({
                patientId
            })
            return response
        } catch (err) {
            console.error(t`Failed to fetch patient data:`, err)
            return null
        }
    }, [])

    // Form state
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        assignedPatient: null
    })

    const [errors, setErrors] = useState<Record<string, string>>({})

    const assignedPatient = formData.assignedPatient

    // Reset form when modal opens/closes
    useEffect(() => {
        const initializeForm = async () => {
            if (open) {
                if (mode === 'edit' && carePartner) {
                    // Edit mode: populate with existing data
                    if (patientId) {
                        // If patientId is provided, fetch real patient info
                        try {
                            const patientData = await fetchPatientData(patientId)
                            setFormData({
                                firstName: carePartner.firstName || '',
                                lastName: carePartner.lastName || '',
                                phoneNumber: carePartner.phoneNumber || '',
                                email: carePartner.email || '',
                                assignedPatient: patientData || null
                            })
                        } catch (err) {
                            // Fallback to placeholder if patient fetch fails
                            setFormData({
                                firstName: carePartner.firstName || '',
                                lastName: carePartner.lastName || '',
                                phoneNumber: carePartner.phoneNumber || '',
                                email: carePartner.email || '',
                                assignedPatient: null
                            })
                        }
                    } else {
                        // No specific patient context - allow user to select any patient
                        setFormData({
                            firstName: carePartner.firstName || '',
                            lastName: carePartner.lastName || '',
                            phoneNumber: carePartner.phoneNumber || '',
                            email: carePartner.email || '',
                            assignedPatient: null
                        })
                    }
                } else {
                    // Create mode: empty form
                    setFormData({
                        firstName: '',
                        lastName: '',
                        phoneNumber: '',
                        email: '',
                        assignedPatient: null
                    })
                }
                setErrors({})
                setSubmitError(null)
            }
        }

        initializeForm()
    }, [open, mode, carePartner, patientId, fetchPatientData])

    const handleClose = () => {
        if (!loading) {
            onClose()
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validate form - require patient only if not in edit mode without patientId
        const requirePatient = !(mode === 'edit' && !patientId)
        const newErrors = validateForm(formData, requirePatient)
        setErrors(newErrors)

        if (Object.keys(newErrors).length > 0) {
            return
        }

        try {
            setSubmitError(null)

            let result
            if (mode === 'edit' && carePartner) {
                // Update existing care partner
                result = await updateCarePartnerData(carePartner.id, {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: formData.phoneNumber,
                    email: formData.email || undefined
                })

                // If a new patient is assigned, associate the care partner with that patient
                if (formData.assignedPatient?.id) {
                    try {
                        await CarePartnersService.associatePatientCarePartnersCarePartnerIdPatientPatientIdPost({
                            carePartnerId: carePartner.id,
                            patientId: formData.assignedPatient.id
                        })
                        console.log(t`Successfully associated care partner with patient`)
                    } catch (err) {
                        console.error(t`Failed to associate care partner with patient:`, err)
                        // Don't throw here - the care partner was updated successfully
                    }
                }
            } else {
                // Create new care partner and associate with selected patient
                const targetPatientId = patientId || formData.assignedPatient?.id
                if (!targetPatientId) {
                    throw new Error(t`No patient selected`)
                }

                result = await createAndAssign(
                    {
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phoneNumber: formData.phoneNumber,
                        email: formData.email || undefined
                    },
                    targetPatientId
                )
            }

            // Call success callback with the care partner ID
            if (onSuccess && result) {
                onSuccess(result.id)
            }

            // Close modal on success
            handleClose()
        } catch (err) {
            setSubmitError(
                mode === 'edit' ? t`Failed to update care partner. Please try again.` : t`Failed to create care partner. Please try again.`
            )
        }
    }

    const formatPhoneNumber = (phone: string) => {
        let digits = phone.replace(/\D/g, '')

        if (digits.length > 10) {
            digits = digits.substring(0, 10)
        }

        if (digits.length === 10) {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
        } else if (digits.length > 6) {
            return digits.replace(/(\d{3})(\d{3})/, '$1-$2-')
        } else if (digits.length > 3) {
            return digits.replace(/(\d{3})/, '$1-')
        }
        return phone
    }

    const updateField = (field: keyof FormData, value: any) => {
        // Format phone number if it's the phone field
        if (field === 'phoneNumber' && typeof value === 'string') {
            value = formatPhoneNumber(value)
        }

        setFormData((prev) => ({ ...prev, [field]: value }))

        // Real-time validation for the updated field
        const updatedFormData = { ...formData, [field]: value }
        const requirePatient = !(mode === 'edit' && !patientId)
        const fieldErrors = validateForm(updatedFormData, requirePatient)

        // Only show error for the current field if it has an error
        if (fieldErrors[field]) {
            setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }))
        } else {
            // Clear error if field is now valid
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h6' component='div'>
                    {mode === 'edit' ? t`Edit Care Partner` : t`Create Care Partner`}
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        color: (theme) => theme.palette.grey[500]
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {loading && <LinearProgress />}

            <form onSubmit={handleSubmit}>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {submitError && (
                            <Alert severity='error' sx={{ mb: 2 }}>
                                {submitError}
                            </Alert>
                        )}

                        {error && (
                            <Alert severity='error' sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <TextField
                            fullWidth
                            label={t`First Name *`}
                            placeholder={t`Enter first name`}
                            value={formData.firstName}
                            onChange={(e) => updateField('firstName', e.target.value)}
                            error={!!errors.firstName}
                            helperText={errors.firstName || ''}
                            disabled={loading}
                            inputProps={{ maxLength: 50 }}
                        />

                        <TextField
                            fullWidth
                            label={t`Last Name *`}
                            placeholder={t`Enter last name`}
                            value={formData.lastName}
                            onChange={(e) => updateField('lastName', e.target.value)}
                            error={!!errors.lastName}
                            helperText={errors.lastName || ''}
                            disabled={loading}
                            inputProps={{ maxLength: 50 }}
                        />

                        <TextField
                            fullWidth
                            label={t`Phone Number *`}
                            placeholder={t`123-456-7890`}
                            value={formData.phoneNumber}
                            onChange={(e) => updateField('phoneNumber', e.target.value)}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                            disabled={loading}
                            inputProps={{ maxLength: 12 }}
                        />

                        <TextField
                            fullWidth
                            label={t`Email Address`}
                            placeholder={t`Enter email address`}
                            value={formData.email}
                            onChange={(e) => updateField('email', e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email || ''}
                            disabled={loading}
                            inputProps={{ maxLength: 256 }}
                        />

                        <FormControl fullWidth error={!!errors.assignedPatient}>
                            <PatientSearchDropdown
                                value={formData.assignedPatient}
                                onChange={(patient) => updateField('assignedPatient', patient)}
                                error={!!errors.assignedPatient}
                                helperText={errors.assignedPatient}
                                disabled={loading || (mode === 'edit' && !!patientId)}
                                label={mode === 'edit' && !patientId ? t`Assigned Patient` : t`Assigned Patient *`}
                                placeholder={t`Search patients by name, DOB, or phone...`}
                            />
                        </FormControl>

                        {assignedPatient && (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant='body2' color='text.secondary'>
                                    <strong>{t`Selected Patient:`}</strong> {assignedPatient.firstName || t`Unknown`}{' '}
                                    {assignedPatient.lastName || t`Unknown`}
                                    {assignedPatient.dateOfBirth && ` (${assignedPatient.dateOfBirth})`}
                                    {assignedPatient.mobilePhone && ` - ${assignedPatient.mobilePhone}`}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={onClose} disabled={loading}>
                        {t`Cancel`}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant='contained'
                        disabled={
                            loading ||
                            !formData.firstName.trim() ||
                            !formData.lastName.trim() ||
                            !formData.phoneNumber.trim() ||
                            (!formData.assignedPatient && !(mode === 'edit' && !patientId))
                        }
                    >
                        {loading
                            ? mode === 'edit'
                                ? t`Updating...`
                                : t`Creating...`
                            : mode === 'edit'
                            ? t`Update Care Partner`
                            : t`Create Care Partner`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
